import { useEffect, useState } from 'react';

import { usePackGroups } from './usePackGroups';

/**
 * Manages pack group selection.
 *
 * @param {Date} date - The date to fetch pack groups for
 * @param {number} initialPackGroupID - The initially selected pack group ID
 * @param {Function} updateQueryParams - Callback function to update the query parameters
 * @returns {Object} - Hook return object
 * @returns {Function} handleSelectPackGroup - Callback function to select a pack group
 * @returns {Array} packGroups - The list of valid pack groups
 * @returns {number} selectedPackGroupID - The currently selected pack group ID
 */
export function usePackGroupSelection(date, initialPackGroupID, updateQueryParams) {
    const [selectedPackGroupID, setSelectedPackGroupID] = useState(initialPackGroupID);
    const { loading: packGroupsLoading, packGroups } = usePackGroups(date);

    // Valid pack groups will always be in the format "BWI1", "CHI1", etc.
    // Pack groups such as "LS BWI1", "CC LAX1", etc., are considered invalid for this tool's use case.
    const validPackGroups = packGroups?.filter((packGroup) => packGroup.name.split(' ').length === 1);

    const handleSelectPackGroup = (packGroupID) => {
        setSelectedPackGroupID(packGroupID);
        updateQueryParams({ packGroupID });
    };

    useEffect(() => {
        const isCurrentSelectionValid = validPackGroups?.some((packGroup) => packGroup.id === selectedPackGroupID);

        if (!packGroupsLoading && !isCurrentSelectionValid) {
            const defaultPackGroupID = validPackGroups?.[0]?.id ?? null;
            setSelectedPackGroupID(defaultPackGroupID);
            updateQueryParams({ packGroupID: defaultPackGroupID });
        }
    }, [validPackGroups, packGroupsLoading, selectedPackGroupID, updateQueryParams]);

    return { handleSelectPackGroup, packGroups: validPackGroups, selectedPackGroupID };
}
